import { FC, useEffect, useState } from 'react';

import {
    Box,
    Button,
    GridPaginationModel,
    Select,
    SelectChangeEvent,
} from '@thirdspacelearning/library';

import InputField from './InputField';

import { SearchParams } from '@types';
import { defaultSearchParams } from '@utils';

const sx = {
    wrapper: {
        display: 'flex',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: 2,
        my: 2,
    },
    select: {
        formControl: {
            width: '200px',
        },
    },
};

interface FilterOption {
    id: number | string;
    name: string;
}

interface Props {
    /*
     * The filters to show to the user and their options, e.g.:
     * - Type - standard, sats, gcse
     * - Year - 1, 2, 3, 4, 5, 6, ...
     */
    filters: Record<
        string,
        {
            type: string;
            label: string;
            options: FilterOption[];
            multiple?: boolean;
            placeholder?: string;
            transformValue?: (value: string) => any;
            resetFields?: Record<string, any>;
        }
    >;
    /**
     * The search params currently in use (these are the ones we send to the BE)
     */
    searchParams: SearchParams;
    /**
     * Function to set the search params
     */
    setSearchParams: (params: SearchParams) => void;
    /**
     * Current data grid pagination model
     */
    paginationModel: GridPaginationModel;
    /**
     * Function to set the data grid pagination model
     * Used to reset the page
     */
    setPaginationModel: (paginationModel: GridPaginationModel) => void;
    /**
     * Reset the filters when this property changes
     * E.g. Country
     */
    resetFiltersOn?: any;

    /**
     * Additional search params to reset when clearing the filters.
     */
    resetAdditionalParams?: { [key: string]: string };
}

const Filters: FC<Props> = ({
    filters,
    searchParams,
    setSearchParams,
    paginationModel,
    setPaginationModel,
    resetFiltersOn,
    resetAdditionalParams = {},
}) => {
    const [selectedFilters, setSelectedFilters] =
        useState<SearchParams>(defaultSearchParams);

    const resetAllFilters = () => {
        // Persistent search params
        const defaultParams = {
            ...searchParams,
            ...defaultSearchParams,
            ...resetAdditionalParams,
        };

        setSelectedFilters(defaultParams);
        setSearchParams(defaultParams);
    };

    const resetPage = () => {
        setPaginationModel({
            ...paginationModel,
            page: 0,
        });
    };

    const getTransformedFilters = (newFilters: any) => {
        return Object.entries(newFilters).reduce(
            (acc, [key, val]) => {
                const config = filters[key];
                if (config?.transformValue) {
                    acc[key] = config.transformValue(val as any);
                } else {
                    acc[key] = val;
                }
                return acc;
            },
            {} as Record<string, unknown>,
        );
    };

    const handleInput = (name: string, value: string) => {
        const newFilters = { ...selectedFilters };
        newFilters[name] = value;

        const transformedFilters = getTransformedFilters(newFilters);

        setSelectedFilters(newFilters);
        setSearchParams({ ...transformedFilters, sort: searchParams.sort });

        resetPage();
    };

    const handleSelect = (event: SelectChangeEvent<string | string[]>) => {
        const { name, value } = event.target;

        if (!name) {
            return;
        }

        // Set new filters
        let newFilters = { ...selectedFilters };

        // Set new value
        newFilters[name] = value;

        const filterConfig = filters[name];

        // Reset fields based on the config
        // E.g. when changing the LO Type we need to reset the year_id
        const resetFields = filterConfig.resetFields;
        if (resetFields) {
            newFilters = {
                ...newFilters,
                ...resetFields,
            };
        }

        resetPage();

        const transformedFilters = getTransformedFilters(newFilters);

        setSelectedFilters(newFilters);
        setSearchParams({
            ...transformedFilters,
            sort: searchParams.sort,
        });
    };

    const onClear = () => {
        resetAllFilters();
    };

    useEffect(() => {
        // Reset selected filters and search params
        // when/if a specific parameter changes
        resetAllFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetFiltersOn]);

    return (
        <Box sx={sx.wrapper}>
            {Object.entries(filters).map(
                ([filter, { type, label, options, multiple, placeholder }]) => {
                    const selectedFilter = selectedFilters[filter];

                    switch (type) {
                        case 'input':
                            return (
                                <InputField
                                    key={filter}
                                    name={filter}
                                    label={label}
                                    value={searchParams[filter] as string}
                                    onSubmit={handleInput}
                                />
                            );
                        case 'select':
                            return multiple ? (
                                <Select
                                    key={filter}
                                    options={options}
                                    value={(selectedFilter as string[]) || []}
                                    name={filter}
                                    label={label}
                                    sx={sx.select}
                                    variant="outlined"
                                    size="small"
                                    handleChange={handleSelect}
                                    multiple
                                    placeholder={`${placeholder} (0)`}
                                    renderValue={(
                                        value: string | string[] | number,
                                    ) =>
                                        `${placeholder} (${(value as string[]).length || 0})`
                                    }
                                />
                            ) : (
                                <Select
                                    key={filter}
                                    options={options}
                                    value={(selectedFilter as string) || ''}
                                    name={filter}
                                    label={label}
                                    sx={sx.select}
                                    variant="outlined"
                                    size="small"
                                    handleChange={handleSelect}
                                />
                            );
                        default:
                            return null;
                    }
                },
            )}
            <Button
                color="primary"
                size="small"
                variant="outlined"
                onClick={onClear}
                data-testid="clear-filters"
            >
                Clear
            </Button>
        </Box>
    );
};

export default Filters;
